import { useEffect, useRef } from 'react'
import {
  Outlet,
  useMatches,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { TransportProvider } from '@connectrpc/connect-query'
import { Trans } from '@lingui/react/macro'
import { addBreadcrumb } from '@sentry/react'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { useFeatures } from '@jotta/grpc-connect-client/config'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { authTransport } from '@jotta/grpc-connect-client/transport'
import { PhotoState } from '@jotta/grpc-connect-openapi/customer'
import { AppLayoutPortal } from '@jotta/ui/AppLayoutPortal'
import { BackButton } from '@jotta/ui/BackButton'
import { BrandIcon, PlainBrandIcon } from '@jotta/ui/BrandIcon'
import SvgAlbum from '@jotta/ui/SvgAlbum'
import SvgCaretLeft from '@jotta/ui/SvgCaretLeft'
import SvgEyeSlash from '@jotta/ui/SvgEyeSlash'
import SvgPhotos from '@jotta/ui/SvgPhotos'
import SvgSharedFolder from '@jotta/ui/SvgSharedFolder'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'
import { isEnumValue } from '@jotta/utils/enum'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { useAlbum } from '../../api/useAlbum'
import { PhotoRouteId } from '../../routes/photoRouteIds'
import { usePhotoStore } from '../../store/PhotoContext'
import { TimelineActivation } from '../PhotoGallery/TimelineActivation'
import { PhotoSearchOverlay } from '../PhotoSearch/PhotoSearchOverlay'
import {
  PhotoUploader,
  UploadPhotosButton,
  UploadPhotosStatus,
} from '../UploadPhotos/UploadPhotos'

const getRouteConfig = (currentRouteId: PhotoRouteId) => {
  switch (currentRouteId) {
    case PhotoRouteId.PhotosRoot:
    case PhotoRouteId.PhotosTimeline:
      return {
        title: <Trans>Photos</Trans>,
        icon: SvgPhotos,
        uploadPhotos: true,
      }
    case PhotoRouteId.PhotosAlbum:
    case PhotoRouteId.PhotosAlbumList:
      return { title: <Trans>Collections</Trans>, icon: SvgAlbum }
    case PhotoRouteId.PhotosAlbumsNormal:
      return {
        title: <Trans>Your albums</Trans>,
        icon: SvgAlbum,
        backPath: '/photo/album',
      }
    case PhotoRouteId.PhotosAlbumsLocation:
      return {
        title: <Trans>Locations</Trans>,
        icon: SvgAlbum,
        backPath: '/photo/album',
      }
    case PhotoRouteId.PhotosAlbumsPeople:
      return {
        title: <Trans>People</Trans>,
        icon: SvgAlbum,
        backPath: '/photo/album',
      }
    case PhotoRouteId.PhotosPublicAlbumInfoLoader:
      return { title: <Trans>Collections</Trans>, icon: SvgAlbum }
    case PhotoRouteId.PhotosAllHidden:
    case PhotoRouteId.PhotosHiddenMedia:
      return { title: <Trans>Hidden</Trans>, icon: SvgEyeSlash }
    case PhotoRouteId.PhotosAlbumsShared:
    case PhotoRouteId.PhotosAllShared:
      return {
        title: <Trans>Shared</Trans>,
        icon: SvgSharedFolder,
      }
    case PhotoRouteId.PhotosShareActionFollowAlbum:
    case PhotoRouteId.PhotosShare:
    case PhotoRouteId.PhotosShareMediaId:
    case PhotoRouteId.PhotosMedia:
    case PhotoRouteId.PhotosShareActionAddPhotos:
    case PhotoRouteId.PhotosAlbumInfoLoader:
    case PhotoRouteId.PhotosPerson:
      return undefined
    default:
      return exhaustiveGuard(currentRouteId)
  }
}
type RouteConfig = ReturnType<typeof getRouteConfig>

const PhotosHeader = ({
  routeConfig,
  showUpload,
  onUpload,
}: {
  routeConfig: RouteConfig
  showUpload: boolean
  onUpload: () => void
}) => {
  const [params, setParams] = useSearchParams()

  if (!routeConfig) {
    return null
  }

  if (params.has('q')) {
    return (
      <AppLayoutPortal slot="moduleNav">
        <div className="flex items-center gap-2">
          <button
            className="btn btn-tonal"
            onClick={e => {
              e.preventDefault()
              params.delete('q')
              setParams(params)
            }}
          >
            <PlainBrandIcon icon={SvgCaretLeft} height={20} />
          </button>
          <span>
            <Trans>Photo Search:</Trans> {params.get('q')}
          </span>
        </div>
      </AppLayoutPortal>
    )
  }

  return (
    <AppLayoutPortal slot="moduleNav">
      <div className="w-full">
        <div className="flex w-full items-center justify-between">
          <div className="flex h-[56px] items-center gap-2 text-lg font-medium text-primary">
            {routeConfig.backPath && (
              <BackButton location={routeConfig.backPath} />
            )}
            <BrandIcon className="size-5" icon={routeConfig.icon} />
            {routeConfig.backPath ? (
              <nav aria-label="Breadcrumb">
                <ol className="flex items-center">
                  <li>
                    <Trans>Collections</Trans>
                  </li>
                  <li
                    className="before:mx-1 before:text-outline before:content-['/']"
                    aria-current="page"
                  >
                    {routeConfig.title}
                  </li>
                </ol>
              </nav>
            ) : (
              routeConfig.title
            )}
          </div>
          {showUpload && (
            <UploadPhotosButton btnType="TONAL" onClick={onUpload} />
          )}
        </div>
      </div>
    </AppLayoutPortal>
  )
}

const PhotosFooter = ({
  showFab,
  onUpload,
}: {
  showFab: boolean
  onUpload: () => void
}) => {
  return (
    <>
      {showFab && (
        <AppLayoutPortal slot="actionButton">
          <UploadPhotosButton btnType="FAB" onClick={onUpload} />
        </AppLayoutPortal>
      )}
      <AppLayoutPortal slot="uploadStatus">
        <UploadPhotosStatus />
      </AppLayoutPortal>
    </>
  )
}

/**
 * Root route container for all photo routes
 */
export const PhotoLayout = observer<{
  isPublic?: boolean
}>(function PhotoLayout({ isPublic = false }) {
  const features = useFeatures()
  const isDesktop = useBreakpoint(Breakpoint.xl)
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchParams] = useSearchParams()
  const openPicker = () => inputRef.current?.click()

  const authenticated = useAuthenticated() === 'TRUE'
  const customer = useGetCustomer().data
  const isPhotosActivated =
    isPublic || (customer && customer.photoState !== PhotoState.NOT_ACTIVE)

  const matches = useMatches()
  const currentRouteIdString = matches[matches.length - 1]?.id
  const routeConfig = isEnumValue(PhotoRouteId, currentRouteIdString)
    ? getRouteConfig(currentRouteIdString)
    : undefined

  // Preserve the desktop search header's existing upload availability.
  const showUploader = Boolean(
    !isPublic &&
    routeConfig?.uploadPhotos &&
    (!isDesktop || !searchParams.has('q')),
  )

  useEffect(() => {
    addBreadcrumb({
      level: 'info',
      category: 'PhotoRouteContainer',
      data: {
        section: isPhotosActivated ? 'PhotoLayout' : 'TimelineActivation',
      },
    })
  }, [isPhotosActivated])

  const searchActivated =
    isPhotosActivated && authenticated && features.photoSearch

  const photoStore = usePhotoStore()
  const { albumId = '', shareId = '' } = useParams()
  const { data: album } = useAlbum(albumId || shareId)
  const currentAlbumId = album?.id || albumId || ''

  useEffect(() => {
    photoStore.currentAlbumId = currentAlbumId
  }, [currentAlbumId, photoStore])

  useEffect(() => {
    photoStore.isPublicRoute = isPublic
  }, [isPublic, photoStore])

  return (
    <TransportProvider transport={authTransport}>
      {showUploader && <PhotoUploader inputRef={inputRef} />}
      <PhotosHeader
        routeConfig={routeConfig}
        showUpload={showUploader && isDesktop}
        onUpload={openPicker}
      />
      <TimelineActivation>
        <Outlet />
      </TimelineActivation>
      {searchActivated && <PhotoSearchOverlay />}

      {!isPublic && (
        <PhotosFooter
          showFab={showUploader && !isDesktop}
          onUpload={openPicker}
        />
      )}
    </TransportProvider>
  )
})
